import { Box, Divider, Pagination } from '@mui/material'
import activateVersionIcon from 'assets/Arrowrew.png'
import eyeIcon from 'assets/Eye.png'
import FilterIcon from 'assets/FilterIcon'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchRationById } from 'store/rationSystemSlice'
import { IRationVersion } from '../../models/IRationSystem'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import TooltipHover from 'ui/TooltipHover'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import RationActivateVersionDrawer from './RationActivateVersionDrawer'

type SortKey = 'date' | 'author'
type SortDir = 'asc' | 'desc'

type RationVersionRow = {
	id: number
	changedAt: number | string
	author: string
}

type SortState = {
	key: SortKey
	dir: SortDir
}

const PAGE_SIZE = 10

const historyColumns = {
	display: 'grid',
	gridTemplateColumns: '180px 1fr 96px',
	alignItems: 'center',
}

const authorColumnWidth = '280px'

const toTimestamp = (value: number | string) => {
	if (typeof value === 'number' && value < 1e12) {
		return value * 1000
	}
	return value
}

const RationVersionHistory = ({
	rationId,
	versions = [],
	activeVersionId,
}: {
	rationId?: number
	versions?: IRationVersion[]
	activeVersionId?: number
}) => {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { userData } = useAppSelector(state => state.userReducer)
	const hasFoodIntegration = Boolean(userData?.company?.food_integration)
	const [visibleVersions, setVisibleVersions] = useState<RationVersionRow[]>([])
	const [sorts, setSorts] = useState<SortState[]>([])
	const [activateTarget, setActivateTarget] = useState<RationVersionRow | null>(
		null,
	)

	const historyRows = useMemo(
		() =>
			versions
				.filter(version => version.id !== activeVersionId)
				.map(version => {
					const nextVersion = versions.find(
						item => item.version_number === (version.version_number ?? 0) + 1,
					)
					return {
						id: version.id,
						changedAt:
							version.archived_at ||
							nextVersion?.created_at ||
							version.created_at ||
							'',
						author:
							nextVersion?.created_by_name || version.created_by_name || '',
					}
				}),
		[versions, activeVersionId],
	)

	const sortedVersions = useMemo(() => {
		const copy = [...historyRows]
		copy.sort((left, right) => {
			for (const sort of sorts) {
				const result =
					sort.key === 'date'
						? moment(toTimestamp(left.changedAt)).valueOf() -
							moment(toTimestamp(right.changedAt)).valueOf()
						: left.author.localeCompare(right.author, 'ru')

				if (result !== 0) {
					return sort.dir === 'asc' ? result : -result
				}
			}
			return 0
		})
		return copy
	}, [historyRows, sorts])

	const toggleSort = (key: SortKey) => {
		setSorts(current => {
			const active = current.find(item => item.key === key)
			if (!active) {
				return [...current, { key, dir: 'asc' }]
			}
			if (active.dir === 'asc') {
				return current.map(item =>
					item.key === key ? { ...item, dir: 'desc' } : item,
				)
			}
			return current.filter(item => item.key !== key)
		})
	}

	const sortColor = (key: SortKey) =>
		sorts.some(item => item.key === key) ? COLORS.main : undefined

	return (
		<>
			<CardStandart>
				<Box
					sx={{
						padding: '24px',
						height: '625px',
						display: 'flex',
						flexDirection: 'column',
						boxSizing: 'border-box',
					}}
				>
					<TypographyHeader>История версий</TypographyHeader>
					<Box
						sx={{ marginTop: '40px', ...historyColumns, paddingBottom: '8px' }}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							<TypographySecondary>Дата и время</TypographySecondary>
							<FilterIcon
								color={sortColor('date')}
								onClick={() => toggleSort('date')}
							/>
						</Box>
						<Box sx={{ display: 'flex', justifyContent: 'center' }}>
							<Box
								sx={{
									width: authorColumnWidth,
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
								}}
							>
								<TypographySecondary>Внес изменения</TypographySecondary>
								<FilterIcon
									color={sortColor('author')}
									onClick={() => toggleSort('author')}
								/>
							</Box>
						</Box>
						<TypographySecondary sx={{ textAlign: 'right' }}>
							Действия
						</TypographySecondary>
					</Box>
					<Divider />
					<Box sx={{ flex: 1, overflow: 'auto' }}>
						{sortedVersions.length === 0 ? (
							<Box
								sx={{
									height: '100%',
									minHeight: '320px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<TypographySecondary>
									Изменений не обнаружено
								</TypographySecondary>
							</Box>
						) : (
							(visibleVersions.length > 0
								? visibleVersions
								: sortedVersions.slice(0, PAGE_SIZE)
							).map(version => {
								const date = moment(toTimestamp(version.changedAt))
								return (
									<Box
										key={`${version.id}-${version.changedAt}`}
										sx={{
											...historyColumns,
											minHeight: '64px',
										}}
									>
										<Box>
											<TypographyPrimary>
												{date.isValid() ? date.format('DD.MM.YY') : '—'}
											</TypographyPrimary>
											<TypographyPrimary>
												{date.isValid() ? date.format('HH:mm:ss') : ''}
											</TypographyPrimary>
										</Box>
										<Box sx={{ display: 'flex', justifyContent: 'center' }}>
											<TypographyPrimary
												sx={{ width: authorColumnWidth, textAlign: 'left' }}
											>
												{version.author || '—'}
											</TypographyPrimary>
										</Box>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'flex-end',
												gap: '12px',
											}}
										>
											<TooltipHover
												title={
													<>
														Просмотр и сравнение состава
														<br />с текущей версией рациона
													</>
												}
												placement='bottom'
												maxWidth='240px'
											>
												<img
													src={eyeIcon}
													alt=''
													style={{
														width: 18,
														height: 18,
														display: 'block',
														cursor: 'pointer',
													}}
													onClick={() =>
														navigate(
															`/feed/ration/${rationId}/version/${version.id}`,
														)
													}
												/>
											</TooltipHover>
											{!hasFoodIntegration && (
												<TooltipHover
													title='Активировать версию рациона'
													placement='bottom'
													oneLine
												>
													<img
														src={activateVersionIcon}
														alt=''
														style={{
															width: 18,
															height: 18,
															display: 'block',
															cursor: 'pointer',
														}}
														onClick={() => setActivateTarget(version)}
													/>
												</TooltipHover>
											)}
										</Box>
									</Box>
								)
							})
						)}
					</Box>
					<Divider />
					{sortedVersions.length === 0 ? (
						<Box
							sx={{
								marginTop: '16px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<TypographyPrimary>
								<span style={{ fontWeight: '600' }}>1 - 1</span>
							</TypographyPrimary>
							<Pagination
								count={1}
								page={1}
								shape='rounded'
								size='small'
								sx={{
									'& .MuiPaginationItem-root': {
										fontSize: '14px',
										width: 20,
										height: 20,
										minWidth: 20,
										margin: '0 2px',
										padding: 0,
									},
									'& .Mui-selected': {
										fontWeight: '600',
										backgroundColor: '#EEE9FA !important',
										color: `${COLORS.main} !important`,
									},
								}}
							/>
						</Box>
					) : (
						<Box sx={{ marginTop: '16px' }}>
							<CustomPagination
								initialArray={sortedVersions}
								setVisibleState={setVisibleVersions}
								count={PAGE_SIZE}
							/>
						</Box>
					)}
				</Box>
			</CardStandart>
			{rationId && activateTarget && (
				<RationActivateVersionDrawer
					open
					onClose={() => setActivateTarget(null)}
					rationId={rationId}
					versionId={activateTarget.id}
					versionDate={moment(toTimestamp(activateTarget.changedAt)).format(
						'DD.MM.YYYY',
					)}
					activeVersionId={activeVersionId}
					onSuccess={() => dispatch(fetchRationById(String(rationId)))}
				/>
			)}
		</>
	)
}

export default RationVersionHistory
